<template>
  <v-dialog
    v-bind="$attrs"
    persistent
    content-class="i-dialog"
    width="750"
    @click:outside="handleCancel"
    @keydown.esc="handleCancel"
  >
    <v-card id="create-consent-dialog">
      <template>
        <v-card-title class="i-dialog-header-div">
          <span class="g-bold g-heading-m">
            Invite {{ isILR ? 'Customer(s)' : 'Retailer(s)' }}
          </span>
        </v-card-title>

        <v-card-text
          class="mt-6 mb-4"
          :class="{ 'i-dialog-content-div-large': $vuetifyDisplay.mdAndUp }"
        >
          <v-autocomplete
            attach
            background-color="#0000000f"
            class="mt-1 mb-4 autocomplete"
            dense
            :disabled="fetchingRetailers"
            hide-details
            :items="retailersList"
            item-text="text"
            item-value="value"
            item-disabled="disabled"
            :label="`Select ${isILR ? 'Customer(s)' : 'Retailer(s)'}`"
            :loading="fetchingRetailers"
            multiple
            outlined
            ref="autocomplete"
            v-model:search-input="searchInput"
            @change="onSelectionChange"
            small-chips
            v-model="selectedStoreIds"
          >
            <!-- SELECT ALL row pinned to the top -->
            <template v-slot:prepend-item>
              <v-list-item @click.stop="toggleAll" class="py-1">
                <v-list-item-action>
                  <v-checkbox
                    :input-value="isAllChecked"
                    :model-value="isAllChecked"
                    :indeterminate="isAllIndeterminate"
                  ></v-checkbox>
                </v-list-item-action>
                <v-list-item-content>
                  <v-row no-gutters align="center">
                    <span style="font-size: 14px !important">
                      All {{ isILR ? 'Customers' : 'Retailers' }}
                    </span>
                  </v-row>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mb-1"></v-divider>
            </template>

            <!-- Selected chips -->
            <template v-slot:selection="{ item }">
              <v-chip small close @click:close="handleUnselectSupplier(item)">{{
                item.text
              }}</v-chip>
            </template>

            <!-- Dropdown items -->
            <template v-slot:item="{ item, attrs, on }">
              <v-list-item
                v-bind="attrs"
                v-on="on"
                :disabled="item.disabled"
                #default="{ active }"
              >
                <v-list-item-action>
                  <v-checkbox
                    :input-value="active"
                    :model-value="active"
                  ></v-checkbox>
                </v-list-item-action>

                <v-list-item-content>
                  <v-row no-gutters align="center">
                    <span style="font-size: 14px !important">{{
                      item.text
                    }}</span>
                    <v-spacer></v-spacer>

                    <v-chip
                      v-if="item.consentStatus === CONSENT_STATUSES.ACCEPTED"
                      class="mx-1 p-status-chip"
                      small
                      color="green"
                      text-color="white"
                      ><strong>ACCEPTED</strong></v-chip
                    >

                    <v-chip
                      v-if="item.consentStatus === CONSENT_STATUSES.CANCELLED"
                      class="mx-1 p-status-chip"
                      small
                      color="red"
                      text-color="white"
                      ><strong>CANCELLED</strong></v-chip
                    >

                    <v-chip
                      v-if="item.consentStatus === CONSENT_STATUSES.PENDING"
                      class="mx-1 p-status-chip"
                      small
                      color="orange"
                      text-color="white"
                      ><strong>PENDING</strong></v-chip
                    >
                  </v-row>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-autocomplete>
          <div v-if="selectedStoreIds.length" class="mb-4 text-right">
            <span class="text-body-2 grey--text">
              {{ selectedStoreIds.length }}
              {{ ` ${isILR ? 'Customer(s)' : 'Retailer(s)'} selected` }}
            </span>
          </div>

          <div class="p-spanded-div"></div>

          <v-alert
            dense
            outlined
            text
            border="left"
            class="mb-2"
            elevation="1"
            type="info"
          >
            <span>
              Once {{ isILR ? 'Customer(s)' : 'Retailer(s)' }} confirm invite,
              your approved Agents in the
              {{ isILR ? 'Customer(s)' : 'Retailer(s)' }} State(s) will be able
              to place Turn In Orders in each
              {{ isILR ? 'Customer(s)' : 'Retailer(s)' }} store the following
              day.
            </span>
          </v-alert>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="i-dialog-actions-div justify-end">
          <v-btn rounded text class="i-btn-text-black" @click="handleCancel">
            <strong>Cancel</strong>
          </v-btn>

          <v-btn
            rounded
            class="i-btn-block-secondary mr-4"
            @click="handleConfirm"
            :disabled="!validInvitation"
            :loading="updating"
          >
            <strong>Invite</strong>
          </v-btn>
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex'

const CONSENT_STATUSES = {
  ACCEPTED: 'ACCEPTED',
  CANCELLED: 'CANCELLED',
  PENDING: 'PENDING'
}

export default {
  data: () => ({
    CONSENT_STATUSES,
    searchInput: '',
    selectedStoreIds: []
  }),

  computed: {
    ...mapState('retailerInvites', [
      'fetchingRetailers',
      'updating',
      'retailersForConsent'
    ]),
    ...mapGetters('tenant', ['isILR']),

    // Normalize items for v-autocomplete
    retailersList() {
      return this.retailersForConsent.map((p) => ({
        ...p,
        disabled:
          p.consentStatus === CONSENT_STATUSES.PENDING ||
          p.consentStatus === CONSENT_STATUSES.ACCEPTED,
        text: p.store.displayName,
        value: p.store.id
      }))
    },

    // Only IDs that are selectable (not disabled)
    allSelectableIds() {
      return this.retailersList.filter((i) => !i.disabled).map((i) => i.value)
    },

    // Enable Invite button only if something selected
    validInvitation() {
      return this.selectedStoreIds.length > 0
    },

    // "All" checkbox states
    isAllChecked() {
      return (
        this.allSelectableIds.length > 0 &&
        this.selectedStoreIds.length === this.allSelectableIds.length
      )
    },
    isAllIndeterminate() {
      return (
        this.selectedStoreIds.length > 0 &&
        this.selectedStoreIds.length < this.allSelectableIds.length
      )
    }
  },

  methods: {
    ...mapActions('retailerInvites', ['fetchRetailersForConsent']),

    // Keep model clean and consistent
    onSelectionChange(val) {
      // Vuetify already filters out disabled via item-disabled
      this.selectedStoreIds = Array.isArray(val) ? val : []
    },

    toggleAll() {
      this.selectedStoreIds = this.isAllChecked
        ? []
        : [...this.allSelectableIds]
    },

    handleCancel() {
      this.$emit('cancel')
      this.selectedStoreIds = []
    },

    handleConfirm() {
      this.$emit('confirm', this.selectedStoreIds)
      this.selectedStoreIds = []
    },

    handleUnselectSupplier(supplier) {
      this.selectedStoreIds = this.selectedStoreIds.filter(
        (id) => id !== supplier.value
      )
    }
  },

  mounted() {
    this.fetchRetailersForConsent()
  },

  watch: {
    // If list updates (or statuses change), drop now-invalid selections
    retailersList() {
      const allowed = new Set(this.allSelectableIds)
      this.selectedStoreIds = this.selectedStoreIds.filter((id) =>
        allowed.has(id)
      )
    }
  }
}
</script>

<style lang="scss" scoped>
#create-consent-dialog {
  .p-spanded-div {
    height: 300px;
  }
  .p-status-chip {
    * {
      font-size: 11px !important;
      letter-spacing: 0.75px;
    }
  }
}
</style>
